import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { AdvancedFiltersContextProvider } from '../../../../../../../components-v2/AdvancedFilters/AdvancedFiltersContext';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../../../../../../../components-v2/Drawer/Drawer';
import { Input } from '../../../../../../../components-v2/Input';
import { Pill } from '../../../../../../../components-v2/Pill';
import { AdvancedFiltersLegacyUI } from '../../../../../../../components/AdvancedFilters/AdvancedFiltersLegacyUI';
import { ButtonBase } from '../../../../../../../components/Button';
import { getDisplayName } from '../../../../../../../../models/NameUtils';
import { useUserSession } from '../../../../../../../../models/hooks/appStateHooks';
import { useContactAutocomplete } from '../../../../../../../../queries/Contact/useContactAutocomplete';
import { ICampaignAudience } from '../../hooks/useCampaignAudiences';
import { styleSheet } from './styles';

interface IProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	campaignName: string;
	audience: ICampaignAudience;
	onApply: (audience: ICampaignAudience) => void;
	campaignKey: string;
	impersonationContext?: Api.IImpersonationContext;
}

export function EditAudienceFlyout({
	open,
	onOpenChange,
	campaignName,
	audience,
	onApply,
	campaignKey,
	impersonationContext,
}: IProps): JSX.Element {
	const userSession = useUserSession();
	const [draftExcluded, setDraftExcluded] = React.useState<Api.IContact[]>(audience.excludedContacts);
	const [excludeFragment, setExcludeFragment] = React.useState('');
	const [showExcludeInput, setShowExcludeInput] = React.useState(false);

	React.useEffect(() => {
		if (open) {
			setDraftExcluded(audience.excludedContacts);
			setExcludeFragment('');
			setShowExcludeInput(false);
		}
	}, [open, audience]);

	const contactAutocompleteQuery = useContactAutocomplete({
		fragment: excludeFragment,
		pageSize: 10,
		enabled: excludeFragment.length > 0,
		impersonationContext,
	});

	const autocompleteResults: Api.IContact[] = contactAutocompleteQuery.data?.pages.flatMap(page => page.values) ?? [];

	const handleExcludeContact = (contact: Api.IContact) => {
		setDraftExcluded(prev => (prev.some(c => c.id === contact.id) ? prev : [...prev, contact]));
		setExcludeFragment('');
		setShowExcludeInput(false);
	};

	const handleRestoreContact = (contactId: string) => {
		setDraftExcluded(prev => prev.filter(c => c.id !== contactId));
	};

	const handleSave = (
		criteria: Api.IContactFilterCriteria[],
		household?: boolean,
		ownership?: Api.IOwnershipFilter
	) => {
		onApply({
			criteria,
			groupByHousehold: household ?? true,
			ownershipFilter: ownership ?? null,
			excludedContacts: draftExcluded,
			suggestedTagNames: [],
		});
		onOpenChange(false);
	};

	const dismiss = () => {
		onOpenChange(false);
	};

	return (
		<Drawer open={open} onOpenChange={onOpenChange} swipeDirection='right'>
			<DrawerContent>
				<DrawerHeader>
					<DrawerTitle>Advanced Filters</DrawerTitle>
				</DrawerHeader>
				<div className={css(styleSheet.editAudienceFlyoutHeader)}>
					<p className={css(styleSheet.editAudienceFlyoutEyebrow)}>EDIT AUDIENCE FOR</p>
					<h3 className={css(styleSheet.editAudienceFlyoutTitle)}>{campaignName}</h3>
				</div>
				<AdvancedFiltersContextProvider
					key={campaignKey}
					initialFilterCriteria={audience.criteria}
					initialOwnershipFilter={audience.ownershipFilter ?? undefined}
					initialHouseholdGroupingOn={audience.groupByHousehold}
					onSave={handleSave}
					refresh={open}
					impersonationContext={impersonationContext}
				>
					<AdvancedFiltersLegacyUI
						hideHeader
						onDismissFlyout={dismiss}
						showPopularTags
						showSavedSearches
						savedSearchesReadOnly
						showHousehold={userSession.account.features?.households?.enabled}
						showOwnershipFilters
						showStatusFilters
						showLocationFilters
						showCompanyTagFilters
						showCustomFields
						showCreateDateFilters
						showLastCommunicationDate
						showKeyFactFilters
						impersonationContext={impersonationContext}
					/>
				</AdvancedFiltersContextProvider>

				<div className={css(styleSheet.editAudienceFlyoutExcludeSection)}>
					<p className={css(styleSheet.editAudienceFlyoutSectionLabel)}>Remove specific contacts</p>
					<p className={css(styleSheet.editAudienceFlyoutExcludeHelper)}>
						Start from the filters above, then drop anyone who shouldn&apos;t receive this one.
					</p>
					{draftExcluded.length > 0 && (
						<div className={css(styleSheet.editAudienceFlyoutChips)}>
							{draftExcluded.map(contact => (
								<Pill
									key={contact.id}
									variant='tag'
									color='red'
									label={getDisplayName(contact)}
									value={contact.id}
									onRemove={handleRestoreContact}
								/>
							))}
						</div>
					)}
					{showExcludeInput ? (
						<div className={css(styleSheet.editAudienceFlyoutAutocompleteWrap)}>
							<Input
								placeholder='Search contacts…'
								value={excludeFragment}
								onChange={ev => setExcludeFragment(ev.target.value)}
								autoFocus
							/>
							{autocompleteResults.length > 0 && (
								<div
									role='listbox'
									aria-label='Contact results'
									className={css(styleSheet.editAudienceFlyoutAutocompleteList)}
								>
									{autocompleteResults.map(contact => (
										<ButtonBase
											key={contact.id}
											role='option'
											aria-selected={false}
											className={css(styleSheet.editAudienceFlyoutAutocompleteOption)}
											onClick={() => handleExcludeContact(contact)}
										>
											{getDisplayName(contact)}
										</ButtonBase>
									))}
								</div>
							)}
						</div>
					) : (
						<ButtonBase
							className={css(styleSheet.editAudienceFlyoutExcludeButton)}
							onClick={() => setShowExcludeInput(true)}
						>
							Exclude a contact
						</ButtonBase>
					)}
				</div>
			</DrawerContent>
		</Drawer>
	);
}
